import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccConditionClinicalStatus = 'active' | 'resolved';

export class PccConditionParams {
	clinicalStatuses?: PccConditionClinicalStatus[];
}

/**
 * Represents a coding entry for a condition code
 */
export class PccConditionCoding {
	/**
	 * The coding system identifier (e.g., "http://snomed.info/sct")
	 */
	system: string;

	/**
	 * The version of the coding system (e.g., "SNOMED 2020-04-01")
	 */
	version: string;

	/**
	 * The code within the coding system
	 */
	code: string;

	/**
	 * Human-readable display name for the code
	 */
	display: string;

	/**
	 * PointClickCare error code
	 */
	errorCode: string;

	/**
	 * PointClickCare error description
	 */
	errorMessage: string;
}

/**
 * Represents a condition (problem or diagnosis) for a patient in PointClickCare
 *
 * SimpliScreen maps the condition to the following string for embedding in the RAG:
 * classificationDescription: {classificationDescription}, clinicalStatus: {clinicalStatus}, comments: {comments}, icd10: {icd10}, icd10Description: {icd10Description}, onsetDate: {onsetDate}, principalDiagnosis: {principalDiagnosis}, rankDescription: {rankDescription}, therapy: {therapy}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/conditions?patientId={patientId}
 */
export class PccCondition extends PccAuditInfo {
	/**
	 * Represents the classification for the condition. Classifications are configurable at the facility level in PointClickCare.
	 */
	classificationDescription: string;

	/**
	 * Represents the clinical status of the condition. Possible values are 'ACTIVE' or 'RESOLVED'.
	 */
	clinicalStatus: 'ACTIVE' | 'RESOLVED';

	/**
	 * Free text notes associated to the condition entered by the user. Notes can be added at the time of adding a condition or after a condition record has been added. Line breaks are represented by \n, carriage returns by \r and a combination of both is represented by \r\n. Backslashes in the text must be represented as \\.
	 */
	comments: string;

	/**
	 * Unique identifier for the patient's condition.
	 */
	conditionId: number;

	/**
	 * Object containing the condition code information
	 */
	conditionCode: { codings: PccConditionCoding[] };

	/**
	 * Represents the ICD10 code for the condition.
	 */
	icd10: string;

	/**
	 * Represents the ICD10 code description.
	 */
	icd10Description: string;

	/**
	 * Represents the date when the condition began. The date format is YYYY-MM-DD.
	 */
	onsetDate: string;

	/**
	 * If there is mapping in the PointClickCare System for the Principal diagnosis we will return a true response for the Principal Diagnosis and all others will return false
	 */
	principalDiagnosis: boolean;

	/**
	 * Represents the rank assigned to the condition. In PointClickCare, a patient can have only one active primary condition at a time. The ranks assigned to conditions can be created and are configurable at the facility level.
	 */
	rankDescription: string;

	/**
	 * Represents if the condition is a therapy diagnosis. This field is disabled by default in PointClickCare. Facilities may choose to enable this field. If this field is disabled, this value is not returned. If this field is enabled, possible values that can be returned are 'true' or 'false'.
	 */
	therapy?: boolean;

	/**
	 * Represents the details of the condition.
	 *
	 * SimpliScreen maps the condition to the following string for embedding in the RAG:
	 * classificationDescription: {classificationDescription}, clinicalStatus: {clinicalStatus}, comments: {comments}, icd10: {icd10}, icd10Description: {icd10Description}, onsetDate: {onsetDate}, principalDiagnosis: {principalDiagnosis}, rankDescription: {rankDescription}, therapy: {therapy}
	 */
	details: string;

	constructor(partial?: Partial<PccCondition>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [`Data Type: Condition`];
		if (this.classificationDescription) details.push(`Classification Description: ${this.classificationDescription}`);
		if (this.clinicalStatus) details.push(`Clinical Status: ${this.clinicalStatus}`);
		if (this.comments) details.push(`Comments: ${this.comments}`);
		if (this.icd10) details.push(`ICD10: ${this.icd10}`);
		if (this.icd10Description) details.push(`ICD10 Description: ${this.icd10Description}`);
		if (this.onsetDate) details.push(`Onset Date: ${this.onsetDate}`);
		if (this.principalDiagnosis) details.push(`Principal Diagnosis: ${this.principalDiagnosis}`);
		if (this.rankDescription) details.push(`Rank Description: ${this.rankDescription}`);
		if (this.therapy) details.push(`Therapy: ${this.therapy}`);
		return details.join(', ');
	}
}
